import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import MedicalCommandCenter from "@/components/MedicalCommandCenter";
import ExportCenter from "@/components/ExportCenter";
import Workspace from "@/components/firm/Workspace";
import CrmCommand, { CrmCaseWidget } from "@/components/firm/CrmCommand";
import {
  Activity, AlertTriangle, ChevronDown, ChevronRight, Loader2, Radar, Zap, HeartPulse,
  Plane, Scale, DollarSign, Megaphone, ArrowLeft, Plus, Trash2, Gavel, RefreshCw, Plug, Sparkles,
} from "lucide-react";

const DOT = { green: "bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.6)]", yellow: "bg-yellow-400", orange: "bg-orange-500", red: "bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.5)]" };
const SEV = { high: "text-red-400 bg-red-500/10 border-red-500/20", medium: "text-amber-400 bg-amber-500/10 border-amber-500/20", low: "text-slate-400 bg-slate-500/10 border-slate-500/20" };
const money = (v) => `$${Number(v || 0).toLocaleString()}`;
const card = "bg-[#0E1624] rounded-3xl border border-[#1E2C45]";
const inputD = "bg-[#152033] border border-[#1E2C45] rounded-xl px-3 py-2 text-xs text-slate-200 placeholder:text-slate-500 outline-none focus:border-blue-500/50";

const Kpi = ({ k, onClick }) => (
  <button onClick={onClick} data-testid={`kpi-${k.key}`}
    className="relative overflow-hidden bg-[#0E1624] rounded-2xl p-4 border border-[#1E2C45] text-left hover:-translate-y-0.5 hover:border-slate-600/60 transition-all">
    <div className={`absolute -top-6 -right-6 h-16 w-16 rounded-full blur-2xl opacity-30 ${k.tone === "red" ? "bg-red-500" : k.tone === "amber" ? "bg-amber-500" : "bg-blue-600"}`} />
    <div className="text-2xl lg:text-3xl font-extrabold tracking-tighter text-white font-heading">
      {k.fmt === "money" ? money(k.value) : k.fmt === "percent" ? `${k.value}%` : k.value}
    </div>
    <div className="mt-1 text-[9px] uppercase tracking-[0.15em] text-slate-400 font-semibold leading-tight">{k.label}</div>
  </button>
);

const RollupRow = ({ r, onOpenMedical, onOpenExport, onOpenCase }) => {
  const [exp, setExp] = useState(false);
  return (
    <div className="border-b border-[#152033] last:border-0" data-testid={`rollup-${r.case_id}`}>
      <button onClick={() => setExp(!exp)} className="w-full flex items-center gap-3 py-3 text-left" data-testid={`rollup-toggle-${r.case_id}`}>
        <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${DOT[r.overall_color]}`} />
        <span className="flex-1 min-w-0">
          <span className="text-sm font-semibold text-white">{r.case_number}</span>
          <span className="text-xs text-slate-500 ml-2 truncate">{r.title}</span>
        </span>
        {r.demand_ready && <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[9px] font-bold uppercase">Demand Ready</span>}
        <span className="text-lg font-extrabold text-white font-heading">{r.overall}%</span>
        {exp ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
      </button>
      {exp && (
        <div className="pb-4 pl-5 space-y-3" data-testid={`rollup-detail-${r.case_id}`}>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {r.dimensions.map((d) => (
              <div key={d.key} className="flex items-center gap-2 text-[11px]">
                <span className={`h-1.5 w-1.5 rounded-full ${DOT[d.color]}`} />
                <span className="flex-1 text-slate-400">{d.label}</span>
                <span className="w-24 h-1.5 rounded-full bg-[#152033] overflow-hidden">
                  <span className={`block h-full ${d.color === "green" ? "bg-emerald-500" : d.color === "yellow" ? "bg-yellow-400" : d.color === "orange" ? "bg-orange-500" : "bg-red-500"}`} style={{ width: `${d.score}%` }} />
                </span>
                <span className="w-9 text-right text-slate-300 font-semibold">{d.score}%</span>
              </div>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3 text-[11px]">
            <div>{r.done.map((x, i) => <div key={i} className="text-emerald-400/90">✔ {x}</div>)}</div>
            <div>{r.missing.map((x, i) => <div key={i} className="text-amber-400/90">⚠ {x}</div>)}
              {r.missing.length === 0 && <div className="text-emerald-400">Nothing outstanding</div>}</div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className="px-2.5 py-1 rounded-full bg-blue-500/10 border border-blue-500/25 text-blue-300 font-semibold" data-testid={`nba-${r.case_id}`}>
              Next Best Action: {r.next_best_action?.label || r.next_task}
            </span>
            {r.estimated_completion_days > 0 && <span className="text-slate-500">Estimated completion: <b className="text-slate-300">{r.estimated_completion_days} days</b></span>}
            <span className="text-slate-500">Pipeline: <b className="text-slate-300">{money(r.pipeline_value)}</b></span>
            <span className="ml-auto flex gap-1.5">
              <button onClick={() => onOpenCase(r)} data-testid={`rollup-open-case-${r.case_id}`} className="px-2.5 py-1 rounded-full bg-[#152033] border border-[#1E2C45] text-slate-300">Case File</button>
              <button onClick={() => onOpenMedical(r)} data-testid={`rollup-open-medical-${r.case_id}`} className="px-2.5 py-1 rounded-full bg-emerald-600/90 text-white font-semibold">Medical Command</button>
              <button onClick={() => onOpenExport(r)} data-testid={`rollup-open-export-${r.case_id}`} className="px-2.5 py-1 rounded-full bg-blue-600/90 text-white font-semibold">Export</button>
            </span>
          </div>
          <CrmCaseWidget caseId={r.case_id} caseNumber={r.case_number} />
        </div>
      )}
    </div>
  );
};

const RuleBuilder = () => {
  const [data, setData] = useState(null);
  const [openGroup, setOpenGroup] = useState("accident_types");
  useEffect(() => { api.get("/firm/command/automation").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <Loader2 className="w-6 h-6 text-blue-500 animate-spin mx-auto my-8" />;
  const { rules, catalog, actions, enforceable } = data;
  const toggle = (group, item) => {
    const list = rules.filters[group] || [];
    const next = list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
    setData({ ...data, rules: { ...rules, filters: { ...rules.filters, [group]: next } } });
  };
  const save = async () => {
    try {
      const { data: res } = await api.put("/firm/command/automation", rules);
      setData(res); toast.success("Automation rules saved");
    } catch { toast.error("Save failed"); }
  };
  const GROUPS = [["accident_types", "Accident Types"], ["medical", "Medical Filters"], ["liability", "Liability Filters"],
    ["insurance", "Insurance Filters"], ["property_damage", "Property Damage"]];
  const selCount = GROUPS.reduce((s, [g]) => s + (rules.filters[g] || []).length, 0);
  return (
    <div className="space-y-4" data-testid="rule-builder">
      <div className={`${card} p-5 flex flex-wrap items-center gap-3`}>
        <label className="flex items-center gap-2 text-sm text-slate-300 font-semibold">
          <input type="checkbox" checked={!!rules.enabled} onChange={(e) => setData({ ...data, rules: { ...rules, enabled: e.target.checked } })} data-testid="automation-enabled" className="accent-blue-500 h-4 w-4" />
          Automation Enabled
        </label>
        <select value={rules.action} onChange={(e) => setData({ ...data, rules: { ...rules, action: e.target.value } })} data-testid="automation-action" className={inputD}>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <span className="text-[11px] text-slate-500">{selCount} filter(s) selected</span>
        <button onClick={save} data-testid="automation-save" className="ml-auto px-5 py-2 rounded-full bg-blue-600 text-white text-xs font-bold">Save Rules</button>
      </div>
      <p className="text-[11px] text-slate-500 px-1" data-testid="automation-note">{data.note} Rear-end auto-accept live: <b className={data.rear_end_auto_accept_live ? "text-emerald-400" : "text-slate-400"}>{data.rear_end_auto_accept_live ? "ACTIVE" : "OFF"}</b></p>
      {GROUPS.map(([g, label]) => (
        <div key={g} className={`${card} p-4`}>
          <button onClick={() => setOpenGroup(openGroup === g ? null : g)} className="w-full flex items-center justify-between text-sm font-semibold text-white" data-testid={`rule-group-${g}`}>
            {label} <span className="text-[10px] text-slate-500">{(rules.filters[g] || []).length} selected {openGroup === g ? "▾" : "▸"}</span>
          </button>
          {openGroup === g && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {catalog[g].map((item) => {
                const on = (rules.filters[g] || []).includes(item);
                const live = (enforceable[g] || []).includes(item);
                return (
                  <button key={item} onClick={() => toggle(g, item)} data-testid={`rule-chip-${item.replace(/\W/g, "")}`}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${on ? (live ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "bg-blue-500/15 border-blue-500/40 text-blue-300") : "bg-[#152033] border-[#1E2C45] text-slate-400 hover:border-slate-600"}`}>
                    {item}{on && !live && <span className="ml-1 text-[8px] text-amber-400/90">· soon</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}
      <div className={`${card} p-4`}>
        <div className="text-sm font-semibold text-white">Geography</div>
        <div className="mt-2 grid sm:grid-cols-3 gap-2">
          {["states", "counties", "zips"].map((k) => (
            <input key={k} value={(rules.filters.geo?.[k] || []).join(", ")} data-testid={`geo-${k}`}
              onChange={(e) => setData({ ...data, rules: { ...rules, filters: { ...rules.filters, geo: { ...rules.filters.geo, [k]: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) } } } })}
              placeholder={`${k[0].toUpperCase()}${k.slice(1)} (comma separated)`} className={inputD} />
          ))}
        </div>
      </div>
      <p className="text-[10px] text-slate-600">Selected chips in <span className="text-emerald-400">green</span> are enforced by the live routing engine today. <span className="text-blue-400">Blue</span> chips are saved — enforcement coming soon, no interface change needed when they activate.</p>
    </div>
  );
};

const LitigationView = ({ onOpenExport }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ case_id: "", type: "statute_of_limitations", label: "", due_date: "" });
  const [offer, setOffer] = useState({ case_id: "", direction: "offer", amount: "", from_party: "" });
  const load = useCallback(() => api.get("/firm/command/litigation").then(({ data }) => setData(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loader2 className="w-6 h-6 text-blue-500 animate-spin mx-auto my-8" />;
  const addDeadline = async () => {
    try { await api.post("/firm/command/deadlines", form); toast.success("Deadline added"); setForm({ ...form, label: "", due_date: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addOffer = async () => {
    try { await api.post("/firm/command/offers", offer); toast.success("Recorded"); setOffer({ ...offer, amount: "", from_party: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delDeadline = async (id) => { try { await api.delete(`/firm/command/deadlines/${id}`); load(); } catch { toast.error("Failed"); } };
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="litigation-command">
      <div className={`${card} p-5`}>
        <div className="text-sm font-semibold text-white flex items-center gap-2"><Gavel className="w-4 h-4 text-blue-400" /> Court Calendar & Statute of Limitations</div>
        <div className="mt-3 grid grid-cols-2 gap-1.5">
          <select value={form.case_id} onChange={(e) => setForm({ ...form, case_id: e.target.value })} data-testid="deadline-case" className={inputD}>
            <option value="">Select case…</option>
            {data.cases.map((c) => <option key={c.case_id} value={c.case_id}>{c.case_number}</option>)}
          </select>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="deadline-type" className={inputD}>
            {data.deadline_types.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
          <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Label *" data-testid="deadline-label" className={inputD} />
          <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="deadline-date" className={inputD} />
        </div>
        <button onClick={addDeadline} data-testid="deadline-save" className="mt-2 px-4 py-1.5 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center gap-1"><Plus className="w-3 h-3" /> Add Deadline</button>
        <div className="mt-3 space-y-1.5 max-h-56 overflow-y-auto text-[11px]">
          {data.deadlines.map((d) => (
            <div key={d.deadline_id} className="flex items-center gap-2 rounded-xl bg-[#152033] px-3 py-2" data-testid={`deadline-row-${d.deadline_id}`}>
              <span className={`px-1.5 rounded text-[9px] font-bold uppercase ${d.type === "statute_of_limitations" ? "bg-red-500/15 text-red-400" : "bg-blue-500/15 text-blue-400"}`}>{d.type.replace(/_/g, " ")}</span>
              <span className="flex-1 text-slate-300 truncate">{d.label} <span className="text-slate-500">· {d.case_number}</span></span>
              <span className="text-slate-400 font-semibold">{d.due_date}</span>
              <button onClick={() => delDeadline(d.deadline_id)} data-testid={`deadline-del-${d.deadline_id}`}><Trash2 className="w-3 h-3 text-slate-600 hover:text-red-400" /></button>
            </div>
          ))}
          {data.deadlines.length === 0 && <p className="text-slate-500">No deadlines tracked yet.</p>}
        </div>
      </div>
      <div className="space-y-4">
        <div className={`${card} p-5`}>
          <div className="text-sm font-semibold text-white">Offer History & Negotiations</div>
          <div className="mt-3 grid grid-cols-2 gap-1.5">
            <select value={offer.case_id} onChange={(e) => setOffer({ ...offer, case_id: e.target.value })} data-testid="offer-case" className={inputD}>
              <option value="">Select case…</option>
              {data.cases.map((c) => <option key={c.case_id} value={c.case_id}>{c.case_number}</option>)}
            </select>
            <select value={offer.direction} onChange={(e) => setOffer({ ...offer, direction: e.target.value })} data-testid="offer-direction" className={inputD}>
              <option value="offer">Insurance Offer</option><option value="counter">Counteroffer</option><option value="demand">Demand Sent</option>
            </select>
            <input type="number" value={offer.amount} onChange={(e) => setOffer({ ...offer, amount: e.target.value })} placeholder="Amount $ *" data-testid="offer-amount" className={inputD} />
            <input value={offer.from_party} onChange={(e) => setOffer({ ...offer, from_party: e.target.value })} placeholder="From (adjuster/carrier)" data-testid="offer-from" className={inputD} />
          </div>
          <button onClick={addOffer} data-testid="offer-save" className="mt-2 px-4 py-1.5 rounded-full bg-blue-600 text-white text-xs font-bold">Record</button>
          <div className="mt-3 space-y-1.5 max-h-40 overflow-y-auto text-[11px]">
            {data.offers.map((o) => (
              <div key={o.offer_id} className="flex items-center gap-2 rounded-xl bg-[#152033] px-3 py-2" data-testid={`offer-row-${o.offer_id}`}>
                <span className={`px-1.5 rounded text-[9px] font-bold uppercase ${o.direction === "demand" ? "bg-blue-500/15 text-blue-400" : o.direction === "counter" ? "bg-amber-500/15 text-amber-400" : "bg-emerald-500/15 text-emerald-400"}`}>{o.direction}</span>
                <span className="flex-1 text-slate-300">{money(o.amount)} <span className="text-slate-500">· {o.case_number}{o.from_party ? ` · ${o.from_party}` : ""}</span></span>
                <span className="text-slate-500">{String(o.created_at).slice(0, 10)}</span>
              </div>
            ))}
            {data.offers.length === 0 && <p className="text-slate-500">No offers recorded yet.</p>}
          </div>
        </div>
        <div className={`${card} p-5`}>
          <div className="text-sm font-semibold text-white">Demand Packages & Exports</div>
          <div className="mt-2 space-y-1.5 max-h-44 overflow-y-auto text-[11px]">
            {data.cases.map((c) => (
              <div key={c.case_id} className="flex items-center gap-2 rounded-xl bg-[#152033] px-3 py-2">
                <span className="flex-1 text-slate-300">{c.case_number} <span className="text-slate-500">· {c.exports.count} export(s){c.exports.latest ? ` · latest: ${c.exports.latest.export_type.replace(/_/g, " ")} v${c.exports.latest.version}` : ""}</span></span>
                <button onClick={() => onOpenExport({ case_id: c.case_id, case_number: c.case_number })} data-testid={`lit-export-${c.case_id}`} className="px-2.5 py-1 rounded-full bg-blue-600/90 text-white text-[10px] font-bold">Export Center</button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const FinancialView = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/firm/command/financials").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <Loader2 className="w-6 h-6 text-blue-500 animate-spin mx-auto my-8" />;
  return (
    <div className="space-y-4" data-testid="financial-command">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[["Settlement Pipeline", money(d.pipeline)], ["Expected Fees (33%)", money(d.expected_fees)],
          ["Provider Balances", money(d.provider_balances)], ["Outstanding Invoices", money(d.invoices.outstanding)],
          ["Active LOPs", d.lop_count]].map(([l, v]) => (
          <div key={l} className={`${card} p-4`}>
            <div className="text-xl font-extrabold text-white font-heading">{v}</div>
            <div className="mt-1 text-[9px] uppercase tracking-[0.15em] text-slate-400 font-semibold">{l}</div>
          </div>
        ))}
      </div>
      <div className={`${card} p-5`}>
        <div className="text-sm font-semibold text-white">Pipeline by Case</div>
        <div className="mt-2 space-y-1.5 max-h-72 overflow-y-auto text-[11px]">
          {d.cases.filter((c) => c.pipeline_value > 0 || c.demand_ready).map((c) => (
            <div key={c.case_id} className="flex items-center gap-2 rounded-xl bg-[#152033] px-3 py-2" data-testid={`fin-case-${c.case_id}`}>
              <span className="flex-1 text-slate-300">{c.case_number}</span>
              {c.demand_ready && <span className="px-1.5 rounded bg-emerald-500/15 text-emerald-400 text-[9px] font-bold">DEMAND READY</span>}
              <span className="text-slate-400">{c.overall}% ready</span>
              <span className="text-white font-semibold w-24 text-right">{money(c.pipeline_value)}</span>
            </div>
          ))}
          {d.cases.every((c) => !c.pipeline_value && !c.demand_ready) && <p className="text-slate-500">Log medical bills to build the settlement pipeline.</p>}
        </div>
      </div>
    </div>
  );
};

const DroneView = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/firm/command/drone").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return <Loader2 className="w-6 h-6 text-blue-500 animate-spin mx-auto my-8" />;
  return (
    <div className="space-y-4" data-testid="drone-command">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[["Available Pilots", d.available_pilots], ["Live Missions", d.live_missions],
          ["Completed Flights", d.completed_missions], ["Drone Evidence Files", d.drone_evidence_files]].map(([l, v]) => (
          <div key={l} className={`${card} p-4`}>
            <div className="text-2xl font-extrabold text-white font-heading">{v}</div>
            <div className="mt-1 text-[9px] uppercase tracking-[0.15em] text-slate-400 font-semibold">{l}</div>
          </div>
        ))}
      </div>
      <div className={`${card} p-5`}>
        <div className="text-sm font-semibold text-white flex items-center gap-2"><Plane className="w-4 h-4 text-blue-400" /> Mission Queue & History</div>
        <div className="mt-2 space-y-1.5 max-h-72 overflow-y-auto text-[11px]">
          {d.missions.map((m) => (
            <div key={m.dispatch_id} className="flex items-center gap-2 rounded-xl bg-[#152033] px-3 py-2" data-testid={`mission-${m.dispatch_id}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${["completed"].includes(m.status) ? "bg-emerald-500" : ["offered", "accepted", "en_route", "on_scene"].includes(m.status) ? "bg-amber-400 animate-pulse" : "bg-slate-600"}`} />
              <span className="flex-1 text-slate-300">{m.case_number || m.case_id}</span>
              <span className="text-slate-400 uppercase text-[9px] font-bold">{(m.status || "").replace(/_/g, " ")}</span>
              <span className="text-slate-500">{String(m.created_at || "").slice(0, 10)}</span>
            </div>
          ))}
          {d.missions.length === 0 && <p className="text-slate-500">No drone missions on your cases yet. Drone dispatch triggers automatically from qualifying client accident reports.</p>}
        </div>
      </div>
    </div>
  );
};

const AcquisitionView = ({ onAction }) => {
  const navigate = useNavigate();
  const items = [
    { label: "Firm Marketplace", desc: "RN-vetted live case marketplace", cb: () => navigate("/marketplace"), id: "marketplace" },
    { label: "Auto-Accept Engine", desc: "Rear-end priority instant claim (live)", cb: () => onAction("autoaccept"), id: "autoaccept" },
    { label: "Referrals & Acceptance History", desc: "All referred cases, accept / decline", cb: () => onAction("cases"), id: "referrals" },
    { label: "Client Automation Rules", desc: "Program your intake department", cb: () => onAction({ view: "automation" }), id: "automation" },
    { label: "Billing & Subscription", desc: "Membership, invoices, payment history", cb: () => navigate("/attorney/billing"), id: "billing" },
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="acquisition-command">
      {items.map((i) => (
        <button key={i.id} onClick={i.cb} data-testid={`acq-${i.id}`} className={`${card} p-5 text-left hover:-translate-y-0.5 hover:border-blue-500/30 transition-all`}>
          <div className="text-sm font-semibold text-white">{i.label}</div>
          <div className="mt-1 text-[11px] text-slate-500">{i.desc}</div>
        </button>
      ))}
    </div>
  );
};

const DEPTS = [
  { view: "acquisition", label: "Client Acquisition", icon: Megaphone, desc: "Marketplace · referrals · auto-claim" },
  { view: "automation", label: "Client Automation", icon: Zap, desc: "Program your intake rules" },
  { view: "telehealth", label: "Telehealth Command", icon: HeartPulse, desc: "RN · sessions · records · AI", action: "telehealth" },
  { view: "drone", label: "Drone Command", icon: Plane, desc: "Pilots · missions · evidence" },
  { view: "medical", label: "Medical Command", icon: Activity, desc: "Per-case medical cockpits" },
  { view: "litigation", label: "Litigation Command", icon: Scale, desc: "Demands · deadlines · offers" },
  { view: "financial", label: "Financial Command", icon: DollarSign, desc: "Pipeline · fees · balances" },
  { view: "crm", label: "CRM Intelligence", icon: Plug, desc: "Health · sync · matter links" },
];

export const FirmCommandCenter = ({ view, setView, onAction }) => {
  const [ov, setOv] = useState(null);
  const [medical, setMedical] = useState(null);
  const [exportCase, setExportCase] = useState(null);
  const load = useCallback(() => {
    api.get("/firm/command/overview").then(({ data }) => setOv(data)).catch(() => setOv({ kpis: [], rollup: [], priorities: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (view !== "home") return;
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [view, load]);

  const openMedical = (r) => setMedical({ caseId: r.case_id, caseNumber: r.case_number });
  const openExport = (r) => setExportCase({ caseId: r.case_id || r.caseId, caseNumber: r.case_number || r.caseNumber });
  const downloadReport = async (fmt) => {
    try {
      const res = await api.get(`/firm/command/report?format=${fmt}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `firm_report_${new Date().toISOString().slice(0, 10)}.${fmt}`;
      a.click(); URL.revokeObjectURL(a.href);
      toast.success("Monday Morning Firm Report downloaded");
    } catch { toast.error("Report failed"); }
  };

  const header = (title, sub) => (
    <div className="flex items-center gap-3 mb-4">
      {view !== "home" && <button onClick={() => setView("home")} data-testid="fcc-back" className="p-2 rounded-full bg-[#0E1624] border border-[#1E2C45] text-slate-400 hover:text-white"><ArrowLeft className="w-4 h-4" /></button>}
      <div>
        <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-white font-heading">{title}</h2>
        {sub && <p className="text-xs text-slate-500">{sub}</p>}
      </div>
      {view === "home" && (
        <span className="ml-auto flex items-center gap-1.5">
          <button onClick={() => downloadReport("pdf")} data-testid="report-pdf" className="px-3 py-1.5 rounded-full bg-[#0E1624] border border-[#1E2C45] text-slate-300 text-[10px] font-bold hover:border-blue-500/40">Monday Report PDF</button>
          <button onClick={() => downloadReport("csv")} data-testid="report-csv" className="px-3 py-1.5 rounded-full bg-[#0E1624] border border-[#1E2C45] text-slate-400 text-[10px] font-bold hover:border-blue-500/40">CSV</button>
          <button onClick={load} data-testid="fcc-refresh" className="p-2 rounded-full bg-[#0E1624] border border-[#1E2C45] text-slate-400 hover:text-white"><RefreshCw className="w-4 h-4" /></button>
        </span>
      )}
    </div>
  );

  return (
    <div className="rounded-[2rem] bg-[#060B14] border border-[#1E2C45] p-5 md:p-8 text-slate-200" data-testid="firm-command-center">
      {view === "home" && (
        <>
          {header("Crash Ready Command Center", "Crash Ready Makes Your CRM Smarter™ — the intelligence layer above your firm, its providers and connected CRM")}
          {!ov ? <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="kpi-grid">
                {ov.kpis.map((k) => <Kpi key={k.key} k={k} onClick={() => setView(`ws:${k.key}`)} />)}
              </div>
              <div className="mt-6 grid lg:grid-cols-12 gap-5">
                <div className={`lg:col-span-8 ${card} p-6`} data-testid="firm-rollup">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-white uppercase tracking-[0.12em] flex items-center gap-2"><Radar className="w-4 h-4 text-blue-400" /> Firm-Wide Case Rollup</h3>
                    <span className="text-[10px] text-slate-500">{ov.rollup.length} active</span>
                  </div>
                  <div className="mt-2 max-h-[26rem] overflow-y-auto pr-1">
                    {ov.rollup.map((r) => <RollupRow key={r.case_id} r={r} onOpenMedical={openMedical} onOpenExport={openExport} onOpenCase={(x) => onAction({ action: "cases", caseId: x.case_id })} />)}
                    {ov.rollup.length === 0 && <p className="py-6 text-sm text-slate-500">No active cases yet — claim cases from the marketplace or referrals.</p>}
                  </div>
                </div>
                <div className={`lg:col-span-4 ${card} p-6`} data-testid="priorities-queue">
                  <h3 className="text-sm font-bold text-white uppercase tracking-[0.12em] flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" /> Today's Priorities</h3>
                  <div className="mt-3 space-y-2 max-h-[26rem] overflow-y-auto pr-1">
                    {ov.priorities.map((p, i) => (
                      <div key={i} className={`rounded-xl border px-3 py-2 text-[11px] ${SEV[p.severity]}`} data-testid={`priority-${i}`}>
                        <span className="font-bold uppercase text-[9px] mr-1">{p.severity}</span>{p.detail}
                        <div className="text-[9px] opacity-70 mt-0.5">{p.case_number}</div>
                      </div>
                    ))}
                    {ov.priorities.length === 0 && <p className="text-sm text-slate-500">All clear — no urgent items.</p>}
                  </div>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="dept-nav">
                {DEPTS.map((d) => (
                  <button key={d.view} onClick={() => d.action ? onAction(d.action) : setView(d.view)} data-testid={`dept-${d.view}`}
                    className={`${card} p-4 text-left hover:-translate-y-1 hover:border-blue-500/30 hover:shadow-[0_0_20px_rgba(59,130,246,0.1)] transition-all`}>
                    <d.icon className="w-5 h-5 text-blue-400" />
                    <div className="mt-2 text-xs font-bold text-white leading-tight">{d.label}</div>
                    <div className="mt-0.5 text-[9px] text-slate-500 leading-tight">{d.desc}</div>
                  </button>
                ))}
              </div>
              <details className="mt-6 group" data-testid="why-crash-ready">
                <summary className="cursor-pointer list-none flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-[0.15em] hover:text-blue-400">
                  <Sparkles className="w-4 h-4 text-blue-400" /> Why Crash Ready? — the intelligence layer above your CRM <span className="text-slate-600 group-open:hidden">▸</span><span className="text-slate-600 hidden group-open:inline">▾</span>
                </summary>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2 text-[10px] text-slate-400">
                  {["RN Triage", "Drone Deployments", "Medical Command Center", "AI Medical Summaries", "Treatment Gap Detection",
                    "Demand Package Intelligence", "Litigation Export Engine", "Telehealth Network", "Provider Marketplace",
                    "Evidence Correlation", "Settlement Readiness", "Firm Intelligence", "CRM Health Monitoring", "AI Operational Alerts"].map((x) => (
                    <div key={x} className="rounded-xl bg-[#0E1624] border border-[#1E2C45] px-2.5 py-2"><span className="text-emerald-400">✓</span> {x}</div>
                  ))}
                </div>
                <p className="mt-2 text-[10px] text-slate-600">Crash Ready works WITH your CRM — Filevine, Litify, Clio, CASEpeer, Needles, SmartAdvocate, MyCase, PracticePanther, Smokeball. Your CRM stays the system of record until you choose to move more workflow into Crash Ready.</p>
              </details>
            </>
          )}
        </>
      )}
      {view.startsWith("ws:") && (
        <Workspace wsKey={view.slice(3)} onBack={() => setView("home")}
          onOpenCase={(r) => onAction({ action: "cases", caseId: r.case_id })}
          onOpenMedical={(r) => openMedical(r)} onOpenExport={(r) => openExport(r)} />
      )}
      {view === "crm" && <>{header("CRM Intelligence", "Crash Ready works WITH your CRM — health, sync and matter links")}<CrmCommand /></>}
      {view === "automation" && <>{header("Client Automation Command", "Program your intake department — rules save instantly")}<RuleBuilder /></>}
      {view === "litigation" && <>{header("Litigation Command", "Demands · court calendar · statute of limitations · negotiations")}<LitigationView onOpenExport={(c) => setExportCase({ caseId: c.case_id, caseNumber: c.case_number })} /></>}
      {view === "financial" && <>{header("Financial Command", "Pipeline · fees · balances · invoices")}<FinancialView /></>}
      {view === "drone" && <>{header("Drone Command", "Pilots · live missions · aerial evidence")}<DroneView /></>}
      {view === "acquisition" && <>{header("Client Acquisition Command", "All incoming business in one place")}<AcquisitionView onAction={(a) => typeof a === "string" ? onAction(a) : setView(a.view)} /></>}
      {view === "medical" && (
        <>{header("Medical Command", "Open a per-case medical cockpit")}
          <div className="space-y-2" data-testid="medical-command-list">
            {(ov?.rollup || []).map((r) => (
              <div key={r.case_id} className={`${card} px-4 py-3 flex items-center gap-3`}>
                <span className={`h-2 w-2 rounded-full ${DOT[r.overall_color]}`} />
                <span className="flex-1 text-sm text-slate-300">{r.case_number} <span className="text-slate-500 text-xs">medical {r.dimensions.find((d) => d.key === "medical_readiness")?.score}%</span></span>
                <button onClick={() => openMedical(r)} data-testid={`med-open-${r.case_id}`} className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-bold">Open Medical Command</button>
              </div>
            ))}
          </div>
        </>
      )}
      {medical && <MedicalCommandCenter caseId={medical.caseId} caseNumber={medical.caseNumber} onClose={() => setMedical(null)}
        onOpenExport={() => setExportCase(medical)} onOpenDigest={() => {}} />}
      {exportCase && <ExportCenter caseId={exportCase.caseId} caseNumber={exportCase.caseNumber} onClose={() => setExportCase(null)} />}
    </div>
  );
};

export default FirmCommandCenter;
